import { CurrencySymbol, SetitingsShopCustomType } from 'models';
import { IMAGE_BASE_URL, HOST_COLLECTION_URL } from '../constants';
export const formatMoney = (value: number) => {
  if (!value) {
    return '¥0';
  }
  return ' ¥' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const currencyMap = {
  '₩': { locale: 'ko-KR', currency: 'KRW' },
  '¥': { locale: 'ja-JP', currency: 'JPY' },
  '₫': { locale: 'vi-VN', currency: 'VND' },
  $: { locale: 'en-US', currency: 'USD' },
  '€': { locale: 'de-DE', currency: 'EUR' },
  '฿': { locale: 'th-TH', currency: 'THB' },
};
export function getCurrencyFormat(currency: CurrencySymbol) {
  const result = currencyMap[currency] || { locale: 'ja-JP', currency: 'JPY' };
  return {
    locale: result.locale,
    options: { style: 'currency', currency: result.currency },
  };
}
export const formatNumber = (amount: number = 0) => {
  return `${amount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

export const getImageUrl = (type: string, size: 'small', image: string) => {
  return `${IMAGE_BASE_URL}/${type}/${size}/${image}`;
};
export const getShopAvatarUrl = (size: 'small', image: string) => {
  return `${HOST_COLLECTION_URL}/upload/shop/logo/${size}/${image}`;
};
export const useDebounce = <T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export function getSelectedProductOptions(
  options: any[] = [],
  selectedOptions: any[] = [],
  type: 'name' | 'price'
) {
  let optionArray = selectedOptions.map((item) => {
    const found = options.find((i) => i.id === item.option_type_id);
    if (found) {
      const optionFound = found.options.find((i: any) => i.id === item.option_id);
      return optionFound;
    }
  });
  optionArray = optionArray.filter((i) => i);

  if (type === 'name') {
    return optionArray.map((i) => i.name);
  }

  const optionPrice = optionArray.map((i) => i.price).reduce((a, b) => a + b, 0);
  return optionPrice;
}

export function moneyFormat(money: number, currency: CurrencySymbol) {
  const format = getCurrencyFormat(currency);
  const { locale, options } = format;
  return money.toLocaleString(locale, options as any);
}

export function getPriceWithRounding(total: number, settings: any) {
  if (settings.type_rounding === 0) {
    if (settings.option_rounding > 0) {
      let smallerMultiple =
        parseInt(`${total / (settings.option_rounding * 10)}`, 10) * settings.option_rounding * 10;
      let largerMultiple = smallerMultiple + settings.option_rounding * 10;
      return total - smallerMultiple >= largerMultiple - total ? largerMultiple : smallerMultiple;
    } else {
      if (settings.option_rounding === 1) {
        const round = settings.type_rounding || 1;
        return Math.ceil(total / round) * round;
      }

      if (settings.option_rounding === 2) {
        const round = settings.type_rounding || 1;
        return Math.floor(total / round) * round;
      }
    }
  } else {
    if (settings.option_rounding === 1) {
      const round = settings.type_rounding || 100;
      return Math.ceil(total / round) * round;
    }

    if (settings.option_rounding === 2) {
      const round = settings.type_rounding || 100;
      return Math.floor(total / round) * round;
    }
  }

  return Math.ceil(total);
}

export const totalProductPrice = ({
  taxCalc,
  notCalcExclude,
  bill,
  details,
  selectedShop,
  no_take_out,
}: any) => {
  let productInBill = details || [];
  let billInfo = bill || null;

  const VAT_OF_TAKE_OUT = 0.08;
  let TAX = billInfo?.tax / 100;
  let VAT = billInfo?.consumption_tax / 100;

  if (billInfo?.tax_exclude && !notCalcExclude) {
    let products = productInBill
      .filter((i: any) => i.product_id)
      .map((i: any) => {
        const optionPrice = getSelectedProductOptions(
          i.product?.product_options,
          i.product?.selected_options,
          'price'
        );
        let price = (parseInt(i.price, 10) + optionPrice) * i.quantity;
        if (i.take_out) price = price / (1 + VAT_OF_TAKE_OUT);
        else price = price / (1 + VAT);
        return {
          ...i,
          amount: price,
        };
      });

    products = products.map((i: any) => {
      let price = i.amount;
      if (taxCalc) {
        if (i.take_out) price = price * VAT_OF_TAKE_OUT;
        else price = price * VAT;
      }
      return price;
    });

    let total = products.reduce((a: any, b: any) => a + b, 0);
    if (billInfo.tax_exclude && !taxCalc) {
      total = parseInt(`${Math.ceil(total)}`, 10);
    }
    return total || 0;
  }

  const splitPrice = billInfo?.set_menu?.find(
    (i: any) => i?.set_customer_option?.product_split_price === 1
  );

  const products = productInBill
    .filter((i: any) => i.product_id)
    .map((i: any) => {
      const optionPrice = getSelectedProductOptions(
        i.product?.product_options,
        i.product?.selected_options,
        'price'
      );

      let price = 0;

      if (splitPrice && i.price < 50000) {
        price = (parseInt(`${i.price / 2}`, 10) + optionPrice) * i.quantity;
      } else {
        price = (parseInt(i.price, 10) + optionPrice) * i.quantity;
      }

      if (taxCalc === 'VAT') {
        if (i.vat_calculated) price = 0;
        else if (i.take_out) price = price * VAT_OF_TAKE_OUT;
        else price = price * VAT;
      } else if (taxCalc === 'TAX') {
        if (billInfo?.calculation_tax_type === 1) {
          if (i.tax_calculated) price = 0;
          else price = price * TAX;
        } else {
          if (i.tax_calculated) price = 0;
          else price = (price + price * VAT) * TAX;
        }
      }
      if (no_take_out && i?.take_out) return 0;

      return price;
    });

  const total = products.reduce((a: any, b: any) => a + b, 0);
  return total || 0;
};

export function getPriceWithCardFeeRounding(total: any, settings: any) {
  if (settings.card_fee_type_rounding === 0) {
    if (settings.card_fee_option_rounding > 0) {
      let smallerMultiple =
        parseInt(`${total / (settings.card_fee_option_rounding * 10)}`, 10) *
        settings.card_fee_option_rounding *
        10;
      let largerMultiple = smallerMultiple + settings.card_fee_option_rounding * 10;
      return total - smallerMultiple >= largerMultiple - total ? largerMultiple : smallerMultiple;
    } else {
      if (settings.card_fee_option_rounding === 1) {
        const round = settings.card_fee_type_rounding || 1;
        return Math.ceil(total / round) * round;
      }

      if (settings.card_fee_option_rounding === 2) {
        const round = settings.card_fee_type_rounding || 1;
        return Math.floor(total / round) * round;
      }
    }
  } else {
    if (settings.card_fee_option_rounding === 1) {
      const round = settings.card_fee_type_rounding || 100;
      return Math.ceil(total / round) * round;
    }

    if (settings.card_fee_option_rounding === 2) {
      const round = settings.card_fee_type_rounding || 100;
      return Math.floor(total / round) * round;
    }
  }

  return Math.ceil(total);
}

export const getTotalPaymentWithRounding = ({
  finalTotal,
  billInfo,
  isWithoutRounding,
  noCardFee,
  roundingOne,
  cardMoney,
  adjustmentPrice,
  position,
  shopSettings,
}: any) => {
  let adjustmentInput = 0;

  console.log(finalTotal, shopSettings);
  if (adjustmentPrice?.value) {
    adjustmentInput =
      adjustmentPrice.adjustmentType === '-' ? -adjustmentPrice.value : +adjustmentPrice.value;
  }

  if (isWithoutRounding) {
    if (noCardFee === 'noCardFee') {
      return parseInt(finalTotal, 10);
    }
    return parseInt(finalTotal + getCardFee(cardMoney || 0) + adjustmentInput, 10);
  }

  // Total no CardFee
  if (noCardFee === 'noCardFee') {
    return getPriceWithRounding(finalTotal, shopSettings.data) + adjustmentInput;
  }

  // Rounding only one time
  if (shopSettings.data.rounding_one || roundingOne === 'rounding-one') {
    return (
      getPriceWithRounding(finalTotal, shopSettings.data) +
      getCardFee(cardMoney || 0) +
      adjustmentInput
    );
  }

  if (
    shopSettings.data.enable_card_fee_rounding &&
    shopSettings.data.option_rounding &&
    shopSettings.data.card_fee_option_rounding
  ) {
    return (
      getPriceWithCardFeeRounding(
        getPriceWithRounding(finalTotal, shopSettings.data) + getCardFee(cardMoney || 0),
        shopSettings.data
      ) + adjustmentInput
    );
  }

  return (
    getPriceWithRounding(
      getPriceWithRounding(finalTotal, shopSettings.data) + getCardFee(cardMoney || 0),
      shopSettings.data
    ) + adjustmentInput
  );
};

export const getCardFee = ({ finalTotal, rounding, type, billInfo, state, shopSettings }: any) => {
  let result = 0;
  if (billInfo?.card_fee_type === 'free' || type === 'free-fee-card') {
    result = 0;
    return result;
  }

  const isCard = state ? state.cardEnabled : billInfo?.card_money;
  if (isCard) {
    if (billInfo?.card_fee_type === 'amount') {
      result = billInfo?.card_fee;
    } else {
      result = Math.floor((billInfo?.card_fee / 100) * finalTotal);
    }
  }

  if (shopSettings.data.rounding_one || rounding !== 'rounding') {
    return result;
  }

  if (
    shopSettings.data.enable_card_fee_rounding &&
    shopSettings.data.option_rounding &&
    shopSettings.data.card_fee_option_rounding
  ) {
    return getPriceWithCardFeeRounding(result, shopSettings.data);
  }
  return getPriceWithRounding(result, shopSettings.data);
};

export function checkSettingCustom({
  settingsShopCustom,
  check,
}: {
  settingsShopCustom: SetitingsShopCustomType[];
  check: string;
}) {
  if (settingsShopCustom && check) {
    const checkData = settingsShopCustom.find((item) => item.name === check);
    if (!!checkData?.active) return true;
    else return false;
  }
  return false;
}
